// Persistent list of the user's most recent AI queries, surfaced on
// the Dashboard as one-tap chips under an empty search bar.
//
// Design choices:
//   - Stored client-side (localStorage). Server persistence would let
//     us sync across devices but it's not worth another endpoint for
//     ~5 short strings per user — and privacy-conscious users
//     appreciate that recent searches never leave their device.
//   - Keyed per user so that shared devices / test accounts don't
//     leak queries across users. Falls back to an anonymous key if
//     no user id is provided so nothing crashes for logged-out flows.
//   - Fixed capacity of 5 (LIFO with dedupe): pushing an existing
//     query moves it to the front. Keeps the chip row visually calm
//     while still capturing "typed a variant" recency.
//   - Any localStorage exception (Safari private mode, quota, etc.)
//     is swallowed silently — this is a nice-to-have surface, never
//     a blocker.

const MAX = 5;
const keyFor = (userId) => `bs_ai_queries::${userId || 'anon'}`;

export function readAiQueries(userId) {
    try {
        const raw = localStorage.getItem(keyFor(userId));
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.filter(q => typeof q === 'string' && q.trim()).slice(0, MAX) : [];
    } catch (_) {
        return [];
    }
}

export function saveAiQuery(userId, query) {
    const q = (query || '').trim();
    if (!q) return;
    try {
        const existing = readAiQueries(userId);
        // Case-insensitive dedupe so "Bangkok" and "bangkok" don't both
        // land on the chip row and steal a slot from a distinct query.
        const deduped = existing.filter(x => x.toLowerCase() !== q.toLowerCase());
        const next = [q, ...deduped].slice(0, MAX);
        localStorage.setItem(keyFor(userId), JSON.stringify(next));
    } catch (_) { /* localStorage unavailable — skip */ }
}

export function clearAiQueries(userId) {
    try { localStorage.removeItem(keyFor(userId)); } catch (_) { /* ignore */ }
}
